import { useState, type FormEvent } from 'react';
import { Field } from '../ui/Field';
import { ProductPicker } from '../ui/ProductPicker';
import { AutoTextarea } from '../ui/AutoTextarea';
import { useAdmin } from '../AdminContext';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { euro } from '../../core/format/currency';
import type { AgendaAppointment, AgendaCompleteRequest } from '../../core/types/agenda';
type Props = {
  appointment: AgendaAppointment;
  /** Wat ze de vorige keer gebruikte bij deze klant: één tik om over te nemen. */
  previousUsed: string[];
  busy: boolean;
  onSubmit: (req: AgendaCompleteRequest) => void;
  onCancel: () => void;
};

/**
 * Afronden na de behandeling: bewust kort, ze staat net op uit de behandelstoel.
 * Producten zijn optioneel. Het advies komt bij de klant in Mijn omgeving, met een link
 * naar de webshop; dat is de enige plek waar de agenda de webshop raakt.
 */
export function CompleteForm({
  appointment,
  previousUsed,
  busy,
  onSubmit,
  onCancel
}: Props) {
  const {
    catalog
  } = useAdmin();
  const [used, setUsed] = useState<string[]>([]);
  const [advice, setAdvice] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const hasAccount = Boolean(appointment.customer.id);
  const kuur = appointment.customer.kuur;
  const inKuur = Boolean(appointment.session && kuur && kuur.treatmentSlug === appointment.treatmentSlug);
  const treatment = catalog.treatments.find(t => t.slug === appointment.treatmentSlug);
  const toPay = treatment?.price != null ? euro(treatment.price) : 'de prijs van de behandeling';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!busy) onSubmit({
      products: {
        used,
        advice
      },
      treatmentNote: note
    });
  };
  return <form onSubmit={submit} className="space-y-5" data-id="element-393" data-mp="2iows7o67k24n">
      {inKuur && kuur && <p className="rounded-md bg-sand px-3 py-2 text-[13px] text-ink" data-id="element-394" data-mp="3f8dwjjnpc7sa">
          Sessie {appointment.session} van {kuur.total} van de kuur.{' '}
          {kuur.prepaid && !kuur.expired ? <span className="font-medium" data-id="element-395" data-mp="18j248xg8kr2k">Al betaald: niets afrekenen.</span> : kuur.prepaid ? <span data-id="element-396" data-mp="1diy72td76cgj">De vooruitbetaalde kuur is verlopen: reken {toPay} af.</span> : <span data-id="element-397" data-mp="yubqypfqxgo9">Per keer betaald: reken {toPay} af.</span>}
        </p>}
      <Field label="Gebruikt in de salon" as="div" hint="Alleen een notitie in het dossier: zo zie je volgende keer wat er bij deze huid werkte. Boekt geen voorraad af; een nieuwe salonverpakking boek je in Voorraad › In gebruik genomen." data-id="element-398" data-mp="13slmx29aolax">
        <ProductPicker value={used} onChange={setUsed} products={catalog.products} emptyText="Niets vastgelegd." data-id="element-399" data-mp="upubh2gf5pja" />
        {used.length === 0 && previousUsed.length > 0 && <button type="button" onClick={() => setUsed(previousUsed)} className={`${btnGhost} mt-2 -ml-2.5`} data-id="element-400" data-mp="kuj3d2zingki">
            Zelfde als vorige keer ({previousUsed.length})
          </button>}
      </Field>

      <Field label="Advies voor thuis" as="div" hint={hasAccount ? 'Staat daarna bij haar bezoek in Mijn omgeving, met een link om te bestellen.' : 'Gast zonder account: het advies komt in de bevestigingsmail na de behandeling.'} data-id="element-401" data-mp="2f5xxdvzk8262">
        <ProductPicker value={advice} onChange={setAdvice} products={catalog.products} emptyText="Geen advies meegegeven." data-id="element-402" data-mp="dckwz7cmslli" />
      </Field>

      <Field label="Notitie" hint="Intern. De klant ziet dit nooit." data-id="element-403" data-mp="man506us8eia">
        <AutoTextarea value={note} onChange={e => setNote(e.target.value)} minRows={2} className={inputCls} placeholder="Hoe reageerde de huid? Wat volgende keer anders?" data-id="element-404" data-mp="i9l7bugikaif" />
      </Field>

      <div className="flex flex-wrap items-center gap-2" data-id="element-405" data-mp="w7011bdsv4g7">
        <button type="submit" disabled={busy} aria-busy={busy} className={btnPrimary} data-id="element-406" data-mp="rbkoqnvfxglw">
          {busy ? 'Afronden…' : 'Afronden'}
        </button>
        <button type="button" onClick={onCancel} className={btnGhost} data-id="element-407" data-mp="2t1mcmbeyck8w">
          Terug
        </button>
      </div>
    </form>;
}